import React, { useEffect, useState, useMemo } from "react";
import {
  MapPin,
  Plus,
  ChevronRight,
  ArrowLeft,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";

import { CustomHeader } from "../components/AuthHeader";
import BagBarcodeInput from "../components/BarCodeReader";
import { CustomButton } from "../components/CustomButton";
import { ItemServiceSheet } from "../components/CreateNewOrder/ItemServiceSheet";
import { OrderItemCard } from "../components/CreateNewOrder/OrderItemCard";
import { useToast } from "../components/ToastProvider";

import { useAuthStore } from "../features/auth/authStore";
import { useOrderStore } from "../features/orders/orderStore";
import { useInventoryStore } from "../features/inventory/inventoryStore";
import { usePromoStore } from "../features/promo/promoStore";
import { PickType } from "../features/orders/types";

import {
  GET_LOCKER_LOCATIONS,
  GET_CUSTOMER_LOCATIONS,
} from "../features/location/locationQueries";
import { formatPromoDiscount, parsePrice } from "../utils/createNewOrderUtils";

import rushLogo from "../assets/png/rush-logo.png";

// --- Interfaces for TypeScript Safety ---
interface CustomerLocation {
  id: string;
  location: string;
  label: string;
  city?: string;
}

interface CustomerLocationsData {
  customerLocations: CustomerLocation[];
}

interface LockerLocation {
  id: string;
  location: string;
  is_active: boolean;
}

interface LockerLocationsData {
  lockerLocations: LockerLocation[];
}

type Step = "details" | "items" | "payment";
type PaymentMethod = "payment_link" | "card";

const PAYMENT_OPTIONS: Array<{
  id: PaymentMethod;
  label: string;
  description: string;
}> = [
  // {
  //   id: "payment_link",
  //   label: "Payment Link",
  //   description: "Receive a secure payment link to complete your payment",
  // },
  {
    id: "card",
    label: "Card",
    description: "Visa, Mastercard, and local cards",
  },
];

// --- Utilities ---
const formatMoney = (value: number, currency: string) =>
  `${value.toFixed(2)} ${currency}`;

const buildLocationText = (loc: CustomerLocation) => {
  if (!loc) return "";
  return `${loc.location || ""}${loc.city ? `, ${loc.city}` : ""}`;
};

const extractImageName = (image: string) => {
  const value = image.trim();
  if (!value) return "";
  const sanitized = value.split("?")[0].split("#")[0];
  const segments = sanitized.split("/").filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : sanitized;
};

const calculateDiscountAmount = (subtotal: number, promo: any) => {
  if (!promo) return 0;
  const discountValue = Number.parseFloat(promo.discount);
  if (!Number.isFinite(discountValue)) return 0;
  if (promo.discount_type === "round") return discountValue;
  return (subtotal * discountValue) / 100;
};

export default function NewOrderFlow() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuthStore();
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const [skipcashPaymentId, setSkipcashPaymentId] = useState<string | null>(
    null,
  );

  // --- Global Store State ---
  const {
    isLoading: isOrderLoading,
    createOrder,
    error: orderError,
  } = useOrderStore();
  const {
    basketItems,
    searchText,
    setSearchText,
    filteredItems,
    updateServiceQuantity: storeUpdateQuantity,
    isLoading: isInventoryLoading,
    fetchInventories,
    resetQuantities,
  } = useInventoryStore();
  const {
    checkPromo,
    isLoading: isPromoLoading,
    error: promoError,
    promoCodes,
    clearPromo,
  } = usePromoStore();

  // --- Data Fetching (Typed Hooks) ---
  const { data: locationData } = useQuery<CustomerLocationsData>(
    GET_CUSTOMER_LOCATIONS,
    {
      fetchPolicy: "cache-and-network",
    },
  );
  const locations = locationData?.customerLocations ?? [];

  const {
    data: lockerData,
    loading: lockerLoading,
    error: lockerError,
  } = useQuery<LockerLocationsData>(GET_LOCKER_LOCATIONS, {
    fetchPolicy: "cache-and-network",
  });
  const lockerLocations = lockerData?.lockerLocations ?? [];

  // --- Local Component State ---
  const [step, setStep] = useState<Step>("details");
  const [bagNo, setBagNo] = useState("");
  const [pickupType, setPickupType] = useState<PickType>("locker");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedLockerId, setSelectedLockerId] = useState("");
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>("payment_link");
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [promoInput, setPromoInput] = useState("");

  // --- Effects ---
  useEffect(() => {
    void fetchInventories();
  }, [fetchInventories]);

  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  useEffect(() => {
    if (lockerError) showToast("Failed to load locker locations", "error");
  }, [lockerError, showToast]);

  useEffect(() => {
    setPaymentMethod("card");
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get("id");
    const statusId = params.get("statusId");
    const status = params.get("status");

    if (!paymentId || !statusId) return;

    if (statusId === "2" && status === "Paid") {
      showToast("Payment successful! Placing your order...", "success");
      handlePlaceOrder();
    } else if (statusId === "3") {
      showToast("Payment failed. Please try again.", "error");
    } else if (statusId === "4") {
      showToast("Payment cancelled.", "error");
    }
  }, []);

  // --- Derived Calculations ---
  const selectedSavedLocation = locations.find(
    (l: any) => l.id === selectedLocationId,
  );
  const selectedLocker = lockerLocations.find(
    (l: any) => l.id === selectedLockerId,
  );

  const locationText =
    pickupType === "home"
      ? selectedSavedLocation
        ? buildLocationText(selectedSavedLocation)
        : ""
      : selectedLocker
        ? selectedLocker.location
        : "";

  const selectedServiceItems = basketItems.flatMap((item) =>
    item.services
      .filter((svc) => svc.qty > 0)
      .map((svc) => ({
        itemId: item.id,
        itemTitle: item.title,
        serviceId: svc.id,
        serviceTitle: svc.title,
        qty: svc.qty,
        price: svc.price,
        image: item.image,
      })),
  );

  const selectedItemsCount = selectedServiceItems.reduce(
    (sum, item) => sum + item.qty,
    0,
  );
  const subtotal = selectedServiceItems.reduce(
    (sum, entry) => sum + parsePrice(entry.price) * entry.qty,
    0,
  );
  const activePromo = promoCodes[0] ?? null;
  const discountAmount = calculateDiscountAmount(subtotal, activePromo);
  const finalTotal = subtotal - discountAmount;

  const groupedLines = useMemo(
    () =>
      selectedServiceItems.map((line) => ({
        id: `${line.itemId}-${line.serviceId}`,
        title: `${line.itemTitle} - ${line.serviceTitle}`,
        qty: line.qty,
        lineTotal: parsePrice(line.price) * line.qty,
      })),
    [selectedServiceItems],
  );

  // --- Handlers ---
  const handleDetailsNext = () => {
    const errors: Record<string, string> = {};
    if (!bagNo.trim()) errors.bagNo = "Please enter bag number.";
    if (pickupType === "home" && !selectedLocationId)
      errors.location = "Please select an address.";
    if (pickupType === "locker" && !selectedLockerId)
      errors.locker = "Please select a locker.";

    if (Object.keys(errors).length > 0) {
      setDetailErrors(errors);
      return;
    }
    setStep("items");
  };

  const formatPhone = (phone: string) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\s+/g, "").trim();
    if (cleaned.startsWith("+")) return cleaned; // already international
    if (cleaned.startsWith("00")) return "+" + cleaned.slice(2); // 00974... → +974...
    // Qatar numbers starting with 0 → strip 0, add +974
    if (cleaned.startsWith("0")) return "+974" + cleaned.slice(1);
    // No prefix at all → assume Qatar
    return "+974" + cleaned;
  };

  const cardPayment = async () => {
    // Build a temp order ID for SkipCash — NOT stored in DB yet
    const tempOrderId = `ORD-${Date.now()}`;

    localStorage.setItem(
      "pendingOrder",
      JSON.stringify({
        bagNo,
        pickupType,
        locationText,
        note,
        paymentMethod,
        promoCode: activePromo?.code ?? null,
        orderId: tempOrderId,
        garmentDetails: selectedServiceItems,
      }),
    );

    setIsPaymentPending(true);

    try {
      // Step 1: Create SkipCash payment link via your Node backend
      const res = await fetch(
        "https://api.rush.com.qa/payment/create-skipcash",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: finalTotal.toFixed(2),
            firstName: user?.first_name ?? "Customer",
            lastName: user?.last_name ?? "",
            phone: formatPhone(user?.phone ?? ""),
            email: user?.email ?? "",
            orderId: tempOrderId,
          }),
        },
      );

      const data = await res.json();

      if (!data.payUrl || !data.paymentId) {
        showToast("Could not initiate payment. Please try again.", "error");
        setIsPaymentPending(false);
        return;
      }

      setSkipcashPaymentId(data.paymentId);

      // Step 2: Open SkipCash payment page in a popup
      const popup = window.open(
        data.payUrl,
        "skipcash_payment",
        "width=500,height=700",
      );

      // Step 3: Poll every 3s to check payment status
      const interval = setInterval(async () => {
        try {
          // User closed popup manually before paying
          if (popup?.closed) {
            clearInterval(interval);
            setIsPaymentPending(false);
            setSkipcashPaymentId(null);
            showToast("Payment window closed. Please try again.", "error");
            return;
          }

          const verifyRes = await fetch(
            `https://api.rush.com.qa/payment/verify/${data.paymentId}`,
          );
          const verifyData = await verifyRes.json();

          if (verifyData.statusId === 2) {
            // ✅ Payment confirmed — now create the order via your GraphQL mutation
            clearInterval(interval);
            popup?.close();
            setSkipcashPaymentId(null);
            setIsPaymentPending(false);

            // This calls createOrder() → your GraphQL mutation → navigates to success
            await handlePlaceOrder();
          } else if (verifyData.statusId === 3 || verifyData.statusId === 4) {
            // ❌ Failed or cancelled
            clearInterval(interval);
            popup?.close();
            setSkipcashPaymentId(null);
            setIsPaymentPending(false);
            showToast(
              "Payment failed or was cancelled. Please try again.",
              "error",
            );
          }
          // statusId === 1 → still pending, keep polling
        } catch {
          // Silent — network blip, keep polling
        }
      }, 3000);
    } catch {
      showToast("Payment initiation failed. Please try again.", "error");
      setIsPaymentPending(false);
    }
  };

  const handlePlaceOrder = async () => {
    const today = new Date().toISOString().slice(0, 10);

    // Check if coming back from SkipCash redirect
    const pendingRaw = localStorage.getItem("pendingOrder");
    const pending = pendingRaw ? JSON.parse(pendingRaw) : null;

    // Use restored data if available, otherwise use current state
    const orderBagNo = pending?.bagNo ?? bagNo;
    const orderPickupType = pending?.pickupType ?? pickupType;
    const orderLocationText = pending?.locationText ?? locationText;
    const orderNote = pending?.note ?? note;
    const orderPaymentMethod = pending?.paymentMethod ?? paymentMethod;
    const orderPromoCode = pending?.promoCode ?? activePromo?.code ?? null;
    const orderNo = pending?.orderId ?? `ORD-${Date.now()}`;
    const orderItems = pending?.garmentDetails ?? selectedServiceItems; // 👈 restored items

    const garmentDetails = JSON.stringify(
      orderItems.flatMap((item: any) =>
        Array.from({ length: item.qty }, () => ({
          name: item.itemTitle,
          cleaning_types: JSON.stringify([
            {
              title: item.serviceTitle,
              price: item.price.replace(/[^0-9.]/g, ""),
            },
          ]),
          image: extractImageName(item.image),
        })),
      ),
    );

    console.log(garmentDetails);

    try {
      const createdOrder = await createOrder({
        pickupType: orderPickupType,
        servicePreference: orderPaymentMethod,
        orderNo,
        bagNo: orderBagNo.trim(),
        garmentDetails,
        promoCode: orderPromoCode,
        receivedDate: today,
        lockerDate: today,
        deliveryDate: today,
        location: orderLocationText,
        note: orderNote,
      });

      // Clean up
      localStorage.removeItem("pendingOrder");
      resetQuantities();
      clearPromo();

      navigate("/order/success", {
        state: {
          orderId: createdOrder.order_no || createdOrder.id,
          bagBarcode: orderBagNo.trim(),
        },
      });
    } catch (e) {
      showToast("Order creation failed. Please try again.", "error");
    }
  };

  const Header = ({ onBack }: { onBack: () => void }) => (
    <div className="sticky top-0 z-50 h-[76px] bg-white/90 backdrop-blur-md px-6 pt-4 pb-4 flex items-center justify-between">
      <button
        onClick={onBack}
        className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center bg-white shadow-sm"
      >
        <ArrowLeft size={20} className="text-gray-700" />
      </button>
      <img src={rushLogo} alt="Rush" className="h-7 w-auto object-contain" />
    </div>
  );

  const FieldError = ({ field }: { field: string }) =>
    detailErrors[field] ? (
      <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
        <span className="w-1 h-1 rounded-full bg-red-500" />{" "}
        {detailErrors[field]}
      </p>
    ) : null;

  // --- Render Steps ---
  if (step === "details") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col pb-6">
        <Header onBack={() => navigate("/home")} />
        <div className="bg-white px-6 pb-6 shadow-sm rounded-b-[32px] mb-6">
          <CustomHeader
            title="Place a New Order"
            subtitle="Fill in the details to schedule your laundry."
          />
        </div>
        <div className="px-5 space-y-5">
          <BagBarcodeInput
            value={bagNo}
            onChange={setBagNo}
            onScan={setBagNo}
          />
          <FieldError field="bagNo" />

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Pickup & Drop
            </p>
            <div className="grid grid-cols-2 gap-3">
              {(["locker", "home"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setPickupType(t)}
                  className={`rounded-2xl py-4 font-semibold text-sm transition-all ${pickupType === t ? "bg-[#44B6CE] text-white shadow-md" : "bg-white border border-gray-100 text-gray-600"}`}
                >
                  {t === "home" ? "🏠 Home" : "📦 Locker"}
                </button>
              ))}
            </div>
          </div>

          {pickupType === "home" ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700">
                  Select Address
                </p>
                <button
                  onClick={() => navigate("/profile/locations/add")}
                  className="text-xs font-semibold text-[#44B6CE] flex items-center gap-1"
                >
                  <Plus size={14} /> Add New
                </button>
              </div>
              <div className="relative">
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="w-full appearance-none bg-white border border-[#44B6CE] shadow-sm rounded-2xl px-4 py-3 text-sm text-gray-800 outline-none"
                >
                  {locations.map((loc: any) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.location.split(" - ")[0]}
                    </option>
                  ))}
                </select>
                <ChevronRight
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#44B6CE] rotate-90 pointer-events-none"
                />
              </div>
              {locations.length === 0 ? (
                <button
                  type="button"
                  onClick={() => navigate("/profile/locations/add")}
                  className="w-full bg-white border-2 border-dashed border-[#44B6CE]/30 rounded-2xl px-4 py-5 flex items-center gap-4 transition-all active:border-[#44B6CE]/60"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#44B6CE]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#44B6CE]" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-gray-800">
                      No saved addresses
                    </p>
                    <p className="text-xs text-[#44B6CE] font-semibold mt-0.5">
                      + Tap to add your address
                    </p>
                  </div>
                </button>
              ) : selectedSavedLocation ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-3 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-[#44B6CE]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#44B6CE]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">
                      {selectedSavedLocation.location.split(" - ")[0]}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {selectedSavedLocation.location.split(" - ")[1] ||
                        selectedSavedLocation.location}
                    </p>
                  </div>
                </div>
              ) : null}
              <FieldError field="location" />
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700">
                Locker Location
              </p>
              <div className="relative">
                <select
                  value={selectedLockerId}
                  disabled={lockerLoading}
                  onChange={(e) => setSelectedLockerId(e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-100 shadow-sm rounded-2xl px-4 py-4 text-sm outline-none focus:border-[#44B6CE]"
                >
                  <option value="" disabled>
                    {lockerLoading ? "Loading..." : "Select locker location"}
                  </option>
                  {lockerLocations
                    .filter((l: LockerLocation) => l.is_active)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.location}
                      </option>
                    ))}
                </select>
                <ChevronRight
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none"
                />
              </div>
              <FieldError field="locker" />
            </div>
          )}

          <CustomButton
            variant="primary"
            className="w-full py-4 rounded-full font-bold mt-4"
            onClick={handleDetailsNext}
          >
            Next — Select Items
          </CustomButton>
        </div>
      </div>
    );
  }

  if (step === "items") {
    return (
      <div className="bg-gray-50 min-h-screen flex flex-col">
        <Header onBack={() => setStep("details")} />

        {/* ── Top search bar ── */}
        <div className="sticky top-[76px] z-10 bg-white px-6 pb-6 shadow-sm rounded-b-[32px] mb-5">
          <CustomHeader
            title="Select Items"
            subtitle="What are we cleaning for you today?"
          />
          <div className="mt-4 h-11 rounded-xl bg-gray-100 px-3 flex items-center gap-2 text-gray-500">
            <Search className="h-4 w-4" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search items"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
        </div>

        {/* ── items list ── */}
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-4">
          {isInventoryLoading && (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#44B6CE] border-t-transparent animate-spin rounded-full" />
            </div>
          )}
          {filteredItems.map((item) => (
            <OrderItemCard
              key={item.id}
              item={item}
              selectedCount={item.services.reduce((s, v) => s + v.qty, 0)}
              onAdd={() => setSelectedItemId(item.id)}
            />
          ))}
        </div>

        {/* ── Sticky bottom ── */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-5 pt-4 pb-6 space-y-3">
          <textarea
            placeholder="Add any special instructions..."
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-white rounded-xl px-4 py-3 text-sm border border-gray-100 shadow-sm outline-none focus:border-[#44B6CE] resize-none"
          />
          <CustomButton
            variant="primary"
            disabled={selectedItemsCount === 0}
            className={`w-full py-4 rounded-full font-bold ${
              selectedItemsCount === 0 ? "opacity-50" : "shadow-lg"
            }`}
            onClick={() => setStep("payment")}
          >
            {selectedItemsCount === 0
              ? "Select Items"
              : `Continue to Payment (${selectedItemsCount})`}
          </CustomButton>
        </div>

        <ItemServiceSheet
          open={!!selectedItemId}
          item={basketItems.find((i) => i.id === selectedItemId) || null}
          onClose={() => setSelectedItemId(null)}
          onChangeQuantity={(sid, d) =>
            selectedItemId && storeUpdateQuantity(selectedItemId, sid, d)
          }
          onConfirm={() => setSelectedItemId(null)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8 flex flex-col">
      <Header onBack={() => setStep("items")} />
      <div className="bg-white px-6 pb-6 shadow-sm rounded-b-[32px] mb-6">
        <CustomHeader
          title="Payment"
          subtitle="Review your order and choose a method"
        />
      </div>
      <div className="px-5 space-y-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <button
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            className="w-full flex justify-between items-center"
          >
            <h3 className="font-bold text-gray-900">Order Summary</h3>
            {isSummaryExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          {isSummaryExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-50 space-y-3">
              {groupedLines.map((line) => (
                <div key={line.id} className="flex justify-between text-sm">
                  <p className="text-gray-800">
                    {line.title}{" "}
                    <span className="text-gray-400 ml-1">x{line.qty}</span>
                  </p>
                  <p className="font-semibold">
                    {formatMoney(line.lineTotal, "QAR")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          {!activePromo ? (
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Promo code"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none"
              />
              <button
                onClick={() =>
                  promoInput.trim() && checkPromo(promoInput.trim())
                }
                disabled={isPromoLoading}
                className="bg-[#44B6CE] px-5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
              >
                {isPromoLoading ? "..." : "Apply"}
              </button>
            </div>
          ) : (
            <div className="flex justify-between bg-green-50 p-3 rounded-xl border border-green-100">
              <span className="text-sm font-bold text-green-700">
                {activePromo.code} Applied
              </span>
              <button
                onClick={() => {
                  clearPromo();
                  setPromoInput("");
                }}
                className="text-xs text-red-400 font-bold"
              >
                Remove
              </button>
            </div>
          )}
          {promoError && (
            <p className="text-xs text-red-500 mt-2">{promoError}</p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <h3 className="font-bold text-gray-900">Payment Method</h3>
          {PAYMENT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setPaymentMethod(opt.id)}
              className={`w-full p-4 rounded-xl border text-left flex justify-between items-center ${paymentMethod === opt.id ? "border-[#44B6CE] bg-[#44B6CE]/5" : "border-gray-50 bg-gray-50"}`}
            >
              <div>
                <p className="text-sm font-bold">{opt.label}</p>
                <p className="text-xs text-gray-400">{opt.description}</p>
              </div>
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === opt.id ? "border-[#44B6CE]" : "border-gray-300"}`}
              >
                {paymentMethod === opt.id && (
                  <div className="w-2.5 h-2.5 rounded-full bg-[#44B6CE]" />
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex justify-between text-sm text-gray-500">
            <span>Subtotal</span>
            <span>{formatMoney(subtotal, "QAR")}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount</span>
              <span>-{formatMoney(discountAmount, "QAR")}</span>
            </div>
          )}
          <div className="pt-3 border-t flex justify-between items-center">
            <span className="font-bold">Total</span>
            <span className="text-lg font-bold text-[#44B6CE]">
              {formatMoney(finalTotal, "QAR")}
            </span>
          </div>
        </div>

        <CustomButton
          variant="primary"
          className="w-full py-4 rounded-full font-bold"
          onClick={paymentMethod === "card" ? cardPayment : handlePlaceOrder}
          disabled={isOrderLoading || isPaymentPending}
        >
          {isPaymentPending
            ? "⏳ Waiting for Payment..."
            : isOrderLoading
              ? "Processing..."
              : "Pay & Place Order"}
        </CustomButton>
        {orderError && (
          <p className="text-sm text-center text-red-500">{orderError}</p>
        )}
      </div>
    </div>
  );
}
